import type { ReactNode } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { CAMPOS_CON_ASISTENCIA } from "@/lib/ia";
import { cn } from "@/lib/utils";

import { CampoConAsistencia } from "./CampoConAsistencia";
import { EncabezadoNumerado } from "./EncabezadoNumerado";

interface DescripcionDeLasPlantacionesProps {
  informeId: number;
  /** "01" cuando la lista no se dibuja (la propiedad no tiene plantaciones), "02" si no. */
  numero: string;
  descripcion: string;
  onDescripcion: (texto: string) => void;
  validada: boolean;
  onValidada: (validada: boolean) => void;
  disabled: boolean;
  /** La fuente que declaró la IA para el texto, mientras siga siendo el propuesto. */
  fuente?: ReactNode;
  /** La caja plegable «Registro de terreno» (y «Redactar desde terreno») que va bajo el texto. */
  terreno?: ReactNode;
}

/**
 * La tarjeta de la descripción general de las plantaciones.
 *
 * "Mejorar redacción" es el `CampoConAsistencia` que ya existía: la
 * descripción general YA la puede proponer la IA desde "Rellenar con IA", así
 * que no se agrega ningún botón de generación. La casilla "Información
 * validada" existe porque este texto lo puede haber propuesto un modelo, y
 * alguien tiene que hacerse responsable de que dice lo que corresponde.
 */
export function DescripcionDeLasPlantaciones({
  informeId,
  numero,
  descripcion,
  onDescripcion,
  validada,
  onValidada,
  disabled,
  fuente,
  terreno,
}: DescripcionDeLasPlantacionesProps) {
  const pildora = (
    <label
      className={cn(
        "flex w-fit cursor-pointer items-center gap-2 rounded-md border-[1.5px] px-2.5 py-1.5 text-[0.71875rem] font-bold",
        validada
          ? "bg-success-soft/50 border-success-dot/40 text-success-soft-foreground"
          : "bg-background text-ink-muted",
      )}
    >
      <Checkbox
        checked={validada}
        disabled={disabled}
        onCheckedChange={(v) => onValidada(v === true)}
      />
      Información validada
    </label>
  );

  return (
    <section id="plantaciones-descripcion" className="bg-card scroll-mt-5 rounded-xl border-[1.5px] p-5">
      <EncabezadoNumerado
        numero={numero}
        idDelTitulo="plantaciones-descripcion-titulo"
        titulo="Descripción de las plantaciones"
        bajada="Texto general que se imprime en el informe: especies, edad, estado y manejo."
      />

      <div className="mt-3.5 flex min-w-0 flex-col gap-2">
        <Label htmlFor="descripcion-plantaciones" className="sr-only">
          Descripción de las plantaciones de la propiedad
        </Label>
        <CampoConAsistencia
          id="descripcion-plantaciones"
          campo={CAMPOS_CON_ASISTENCIA.descripcionPlantaciones}
          value={descripcion}
          onChange={onDescripcion}
          informeId={informeId}
          rows={5}
          variantDeMejora="outline"
          placeholder="Describe en general las plantaciones presentes en la propiedad…"
          disabled={disabled}
          accionesDespues={pildora}
        />
        {fuente}
        {terreno}
      </div>
    </section>
  );
}
